import axios from "axios";

/**
 * API base URL resolution:
 * - In the browser, ALWAYS use the current page's origin (`window.location.origin`).
 *   This works on every domain Emergent serves us under — preview, staging, custom
 *   (womensrightstracker.org), etc. — because the Kubernetes ingress always routes
 *   `/api/*` on the same hostname to the backend.
 * - REACT_APP_BACKEND_URL is kept as a fallback (used by SSR / build tooling / tests
 *   that may run outside a browser).
 * This avoids the failure mode where a production build had the preview URL baked in,
 * which caused cross-origin CORS errors and "zero data" on the live domain.
 */
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : ENV_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` makes the browser include the httpOnly auth cookie set by /api/auth/login.
const api = axios.create({ baseURL: API, withCredentials: true });

// Legacy localStorage fallback (token-based). New flow uses cookies set by the backend;
// the interceptor only attaches Authorization if a legacy token is still present so existing
// sessions don't break during migration. We clear it on logout / next login.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("receipts_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default api;

export const CATEGORIES = [
  { slug: "reproductive", label: "Reproductive Rights", blurb: "Contraception, IVF, pregnancy care, fertility." },
  { slug: "abortion", label: "Abortion", blurb: "Bans, gestational limits, medication abortion, travel." },
  { slug: "gender_based_violence", label: "Gender-Based Violence", blurb: "Domestic violence, sexual assault, stalking, trafficking." },
  { slug: "childcare", label: "Childcare & Caregiving", blurb: "Subsidies, paid leave, family medical leave, caregiver pay." },
  { slug: "workplace", label: "Workplace & Economic", blurb: "Equal pay, accommodations, discrimination." },
  { slug: "healthcare", label: "Healthcare Access", blurb: "Emergency care, maternal health, coverage." },
  { slug: "education", label: "Education & Title IX", blurb: "Schools, athletics, harassment, pregnancy." },
  { slug: "legal", label: "Legal Protections", blurb: "Harassment, civil rights, voting, due process." },
];

export const STATUSES = [
  "proposed", "passed", "blocked", "signed", "vetoed",
  "enforced", "repealed", "court_ruling", "executive_action",
];

export const ACTION_TYPES = [
  "bill", "executive_order", "court_ruling", "regulation", "agency_action", "policy_memo",
];

export const IMPACTS = ["restricts", "protects", "mixed"];

export const JURISDICTION_LEVELS = ["federal", "state", "international"];

export const US_STATES = [
  "Federal", "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas",
  "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas",
  "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

// Countries that may host documented government actions affecting women's rights.
// Curated list of ~70 widely-tracked jurisdictions; admins can type any country name in free-text fields too.
export const COUNTRIES = [
  "Afghanistan", "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", "Bolivia", "Brazil",
  "Canada", "Chile", "China", "Colombia", "Czech Republic", "Denmark", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Ethiopia", "Finland", "France", "Germany", "Ghana",
  "Greece", "Guatemala", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran",
  "Iraq", "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kenya", "Lebanon", "Malaysia",
  "Mexico", "Morocco", "Netherlands", "New Zealand", "Nicaragua", "Nigeria", "Norway",
  "Pakistan", "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Romania",
  "Russia", "Saudi Arabia", "Senegal", "Singapore", "South Africa", "South Korea", "Spain",
  "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tanzania", "Thailand", "Turkey",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "Uruguay", "Venezuela",
  "Vietnam", "Yemen", "Zimbabwe",
];

// Country code → ISO 3166-1 alpha-2 for storage in `country` field. Frontend uses the full name in `jurisdiction`.
export const COUNTRY_CODES = {
  "United States": "US", "United Kingdom": "GB", "Canada": "CA", "Mexico": "MX",
  // Most international entries are stored with `country` set to the same name for simplicity.
};

export const formatLabel = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
